import * as Sentry from '@sentry/react'
import { TriangleAlert } from 'lucide-react'
import { Component, type ReactNode, useEffect } from 'react'
import { isRouteErrorResponse, Link, useRouteError } from 'react-router'

import { Button } from '@/components/ui/button'
import { Container } from './Container'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface ErrorBoundaryProps {
  children: ReactNode
}

// Class-based error boundary for catching React errors
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />
    }

    return this.props.children
  }
}

// Router error boundary component
export function RouterErrorBoundary() {
  const error = useRouteError()
  // A route response (404, 401, 503) is an expected outcome; anything else is a bug the router swallowed.
  useEffect(() => {
    if (!isRouteErrorResponse(error)) Sentry.captureException(error)
  }, [error])

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <ErrorFallback
          title="404 - Page Not Found"
          message="The page you're looking for doesn't exist or has been moved."
        />
      )
    }

    if (error.status === 401) {
      return <ErrorFallback title="401 - Unauthorized" message="You don't have access to this page." />
    }

    if (error.status === 503) {
      return (
        <ErrorFallback title="503 - Service Unavailable" message="The service is temporarily unavailable." />
      )
    }

    return (
      <ErrorFallback
        title={`${error.status} - ${error.statusText}`}
        message={error.data?.message || 'An error occurred.'}
      />
    )
  }

  return <ErrorFallback error={error instanceof Error ? error : undefined} />
}

// Generic error fallback component
interface ErrorFallbackProps {
  error?: Error | null
  title?: string
  message?: string
}

function ErrorFallback({ error, title, message }: ErrorFallbackProps) {
  const errorTitle = title || 'Oops! Something went wrong'
  const errorMessage =
    message ||
    error?.message ||
    'An unexpected error occurred. Please try refreshing the page or contact support.'

  return (
    <>
      <div className="flex min-h-screen flex-col items-center justify-center py-20">
        <Container className="flex flex-col items-center">
          <div className="max-w-2xl text-center">
            <div className="text-error mb-6 flex items-center justify-center text-9xl font-bold">
              <TriangleAlert className="size-30" />
            </div>
            <h1 className="mb-4 text-4xl font-bold text-gray-500">{errorTitle}</h1>
            <p className="mb-10 text-lg text-gray-500">{errorMessage}</p>
            <div className="flex justify-center gap-4">
              <Button onClick={() => window.location.reload()}>Reload Page</Button>
              <Link to="/">
                <Button variant="outline">Go Home</Button>
              </Link>
            </div>
          </div>
        </Container>
        {error && process.env.NODE_ENV === 'development' && (
          <div className="mb-10">
            <div className="cursor-pointer font-semibold text-gray-900">Error Details (Development Only)</div>
            <pre className="mt-4 overflow-auto text-left text-sm text-gray-700">{error.stack}</pre>
          </div>
        )}{' '}
      </div>
    </>
  )
}
